import { useState, useEffect, useMemo } from 'react';
import { qryCopilotNewsApi } from '@/api/copilot';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { qryType1, qryType2, qryType2StockItem } from '@/api/types/qryCopilotOnTimeData';

dayjs.extend(relativeTime);

interface NewsItem {
  title: string;
  content: string;
  link: string;
  source: string;
  public_time: string;
}

interface UseMarketOverviewParams {
  taskId: string;
  markets: Array<{ label: string; value: string }>;
  usName: string[];
  hsName: string[];
  hkName: string[];
}

interface UseMarketOverviewReturn {
  activeIndex: number;
  activeTab: string;
  marketData: qryType2StockItem[];
  newsData: NewsItem[];
  loading: boolean;
  newsLoading: boolean;
  dialogOpen: boolean;
  setActiveIndex: (index: number) => void;
  setDialogOpen: (open: boolean) => void;
  fetchMarketData: () => Promise<void>;
  fetchNewsData: () => Promise<void>;
  handleMarketRefresh: () => void;
  handleNewsRefresh: () => void;
  formatRelativeTime: (time: string) => string;
  handleToLink: (link: string) => void;
}

/**
 * MarketOverview 组件的数据和状态管理逻辑
 */
export function useMarketOverview({
  markets,
  usName,
  hsName,
  hkName
}: Omit<UseMarketOverviewParams, 'taskId'>): UseMarketOverviewReturn {
  const [activeIndex, setActiveIndex] = useState(0);
  const [marketData, setMarketData] = useState<qryType2StockItem[]>([]);
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const activeTab = useMemo(() => {
    return markets[activeIndex].value;
  }, [activeIndex]);

  // 格式化相对时间
  const formatRelativeTime = (time: string): string => {
    const now = dayjs();
    const targetTime = dayjs(time);
    const diffInSeconds = now.diff(targetTime, 'second');

    if (diffInSeconds < 60) {
      return `${diffInSeconds}秒前`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}分钟前`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}小时前`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}天前`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months}月前`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years}年前`;
    }
  };

  // 获取市场数据
  const fetchMarketData = async () => {
    try {
      setLoading(true);
      setMarketData([]);

      if (activeTab === 'us') {
        const res = await qryCopilotNewsApi({ qryType: 2 });
        const result: qryType2 = JSON.parse(res || '{}');
        const us_stock = (result.us_stock || []).filter((item: qryType2StockItem) =>
          usName.includes(item.名称)
        );
        setMarketData(us_stock);
      } else if (activeTab === 'hs' || activeTab === 'hk') {
        const activeNames = activeTab === 'hs' ? hsName : hkName;
        const res = await qryCopilotNewsApi({ qryType: 2 });
        const result: qryType2 = JSON.parse(res || '{}');
        const cn_stock = (result.us_stock || []).filter((item: qryType2StockItem) =>
          activeNames.includes(item.名称)
        );
        setMarketData(cn_stock);
      }
    } catch (error) {
      console.error('获取市场数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取新闻数据
  const fetchNewsData = async () => {
    try {
      setNewsLoading(true);
      const res = await qryCopilotNewsApi({ qryType: 1 });
      const result: qryType1 = JSON.parse(res || '{news:[]}');
      setNewsData(result.news || []);
    } catch (error) {
      console.error('获取新闻数据失败:', error);
    } finally {
      setNewsLoading(false);
    }
  };

  const handleMarketRefresh = () => {
    fetchMarketData();
  };

  const handleNewsRefresh = () => {
    setNewsData([]);
    fetchNewsData();
  };

  const handleToLink = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  // 当 Tab 切换时，重新获取市场数据
  useEffect(() => {
    console.log('activeTab切换时，重新获取市场数据', activeTab);

    fetchMarketData();
  }, [activeTab]);

  // 组件挂载时获取新闻数据
  useEffect(() => {
    fetchNewsData();
  }, []);

  return {
    activeIndex,
    activeTab,
    marketData,
    newsData,
    loading,
    newsLoading,
    dialogOpen,
    setActiveIndex,
    setDialogOpen,
    fetchMarketData,
    fetchNewsData,
    handleMarketRefresh,
    handleNewsRefresh,
    formatRelativeTime,
    handleToLink
  };
}
